export const DISPLAY_BUILDER_CANVAS_WIDTH = 1000;
export const DISPLAY_BUILDER_CANVAS_HEIGHT = 1000;
export const DEFAULT_SHELF_COUNT = 5;
export const FIXTURE_CORNER_KEYS = ['tl', 'tr', 'br', 'bl'];
export const MIN_FIXTURE_ANGLE = -15;
export const MAX_FIXTURE_ANGLE = 15;
export const MIN_SHELF_SKEW = -15;
export const MAX_SHELF_SKEW = 15;
export const DEFAULT_PRODUCT_SPACING = 0;
export const MIN_SHELF_PRODUCT_SPACING = -25;
export const MAX_SHELF_PRODUCT_SPACING = 50;
export const MIN_SHELF_Y_OFFSET = -25;
export const MAX_SHELF_Y_OFFSET = 25;
export const MIN_SHELF_DEPTH_ROWS = 1;
export const MAX_SHELF_DEPTH_ROWS = 5;
export const MIN_SHELF_DEPTH_RISE = 0;
export const MAX_SHELF_DEPTH_RISE = 30;
export const MIN_SHELF_DEPTH_RUN = -30;
export const MAX_SHELF_DEPTH_RUN = 30;
export const MIN_SHELF_PRODUCT_HEIGHT = 25;
export const MAX_SHELF_PRODUCT_HEIGHT = 125;

export const DEFAULT_SHELF_DEPTH = Object.freeze({
  rows: 1,
  risePercent: 0,
  runPercent: 0,
  productHeightPercent: 100,
});

export const DEFAULT_LAYOUT_FIXTURES = [
  { key: 'left', label: 'Left', left: 5.0, width: 21.0, top: 16.6, bottom: 90.6, shelfCount: DEFAULT_SHELF_COUNT },
  { key: 'center_left', label: 'Center-L', left: 27.5, width: 8.0, top: 16.6, bottom: 90.6, shelfCount: DEFAULT_SHELF_COUNT },
  { key: 'center', label: 'Center', left: 37.0, width: 25.0, top: 10.5, bottom: 88.0, shelfCount: DEFAULT_SHELF_COUNT },
  { key: 'center_right', label: 'Center-R', left: 63.5, width: 8.0, top: 16.6, bottom: 90.6, shelfCount: DEFAULT_SHELF_COUNT },
  { key: 'right', label: 'Right', left: 73.0, width: 20.0, top: 16.6, bottom: 90.6, shelfCount: DEFAULT_SHELF_COUNT },
];

export function cloneDisplayBuilderValue(value) {
  return JSON.parse(JSON.stringify(value || {}));
}

export function slugifyFixtureKey(value, fallback = 'fixture') {
  const slug = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return slug || fallback;
}

export function rectFromPercent(fixture) {
  const top = Number(fixture.top || 0);
  const bottom = Number(fixture.bottom || top);

  return {
    left: Math.round(Number(fixture.left || 0) / 100 * DISPLAY_BUILDER_CANVAS_WIDTH),
    top: Math.round(top / 100 * DISPLAY_BUILDER_CANVAS_HEIGHT),
    width: Math.round(Number(fixture.width || 0) / 100 * DISPLAY_BUILDER_CANVAS_WIDTH),
    height: Math.round(Math.max(0, bottom - top) / 100 * DISPLAY_BUILDER_CANVAS_HEIGHT),
  };
}

export function percentFromRect(rect = {}) {
  const left = Number(rect.left || 0) / DISPLAY_BUILDER_CANVAS_WIDTH * 100;
  const top = Number(rect.top || 0) / DISPLAY_BUILDER_CANVAS_HEIGHT * 100;
  const width = Number(rect.width || 0) / DISPLAY_BUILDER_CANVAS_WIDTH * 100;
  const bottom = top + (Number(rect.height || 0) / DISPLAY_BUILDER_CANVAS_HEIGHT * 100);

  return {
    left: Number(left.toFixed(1)),
    width: Number(width.toFixed(1)),
    top: Number(top.toFixed(1)),
    bottom: Number(bottom.toFixed(1)),
  };
}

function finiteNumber(value, fallback = 0) {
  const number = Number(value);

  return Number.isFinite(number) ? number : fallback;
}

export function clampDisplayBuilderNumber(value, min, max, fallback = 0) {
  return Math.min(max, Math.max(min, finiteNumber(value, fallback)));
}

export function normalizeShelfDepth(depth = {}) {
  const source = depth && typeof depth === 'object' ? depth : {};

  return {
    rows: Math.round(clampDisplayBuilderNumber(
      source.rows,
      MIN_SHELF_DEPTH_ROWS,
      MAX_SHELF_DEPTH_ROWS,
      DEFAULT_SHELF_DEPTH.rows
    )),
    risePercent: clampDisplayBuilderNumber(
      source.risePercent ?? source.rise_percent,
      MIN_SHELF_DEPTH_RISE,
      MAX_SHELF_DEPTH_RISE,
      DEFAULT_SHELF_DEPTH.risePercent
    ),
    runPercent: clampDisplayBuilderNumber(
      source.runPercent ?? source.run_percent,
      MIN_SHELF_DEPTH_RUN,
      MAX_SHELF_DEPTH_RUN,
      DEFAULT_SHELF_DEPTH.runPercent
    ),
    productHeightPercent: clampDisplayBuilderNumber(
      source.productHeightPercent ?? source.product_height_percent,
      MIN_SHELF_PRODUCT_HEIGHT,
      MAX_SHELF_PRODUCT_HEIGHT,
      DEFAULT_SHELF_DEPTH.productHeightPercent
    ),
  };
}

export function normalizeShelfProductSpacing(value, fallback = null) {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  return clampDisplayBuilderNumber(
    value,
    MIN_SHELF_PRODUCT_SPACING,
    MAX_SHELF_PRODUCT_SPACING,
    fallback ?? DEFAULT_PRODUCT_SPACING
  );
}

export function rectToFixtureCorners(rect = {}) {
  const left = finiteNumber(rect.left);
  const top = finiteNumber(rect.top);
  const width = Math.max(1, finiteNumber(rect.width, 1));
  const height = Math.max(1, finiteNumber(rect.height, 1));

  return {
    tl: { x: left, y: top },
    tr: { x: left + width, y: top },
    br: { x: left + width, y: top + height },
    bl: { x: left, y: top + height },
  };
}

export function normalizeFixtureCorners(corners, rect = {}) {
  const defaults = rectToFixtureCorners(rect);

  if (!corners || typeof corners !== 'object') {
    return defaults;
  }

  return FIXTURE_CORNER_KEYS.reduce((result, key) => {
    const point = corners[key] || defaults[key];

    result[key] = {
      x: finiteNumber(point?.x, defaults[key].x),
      y: finiteNumber(point?.y, defaults[key].y),
    };

    return result;
  }, {});
}

export function fixtureRectFromCorners(corners, fallbackRect = {}) {
  const normalized = normalizeFixtureCorners(corners, fallbackRect);
  const xs = FIXTURE_CORNER_KEYS.map((key) => normalized[key].x);
  const ys = FIXTURE_CORNER_KEYS.map((key) => normalized[key].y);
  const left = Math.min(...xs);
  const top = Math.min(...ys);

  return {
    left,
    top,
    width: Math.max(1, Math.max(...xs) - left),
    height: Math.max(1, Math.max(...ys) - top),
  };
}

export function fixtureCornersFromAngle(rect = {}, value = 0) {
  const angle = clampDisplayBuilderNumber(value, MIN_FIXTURE_ANGLE, MAX_FIXTURE_ANGLE);
  const corners = rectToFixtureCorners(rect);
  const rise = Math.tan(angle * Math.PI / 180) * Math.max(1, Number(rect.width || 1));

  corners.tl.y -= rise / 2;
  corners.tr.y += rise / 2;
  corners.br.y += rise / 2;
  corners.bl.y -= rise / 2;

  return corners;
}

export function getFixtureAngle(corners, rect = {}) {
  const normalized = normalizeFixtureCorners(corners, rect);
  const topAngle = Math.atan2(
    normalized.tr.y - normalized.tl.y,
    normalized.tr.x - normalized.tl.x
  );
  const bottomAngle = Math.atan2(
    normalized.br.y - normalized.bl.y,
    normalized.br.x - normalized.bl.x
  );

  return Number((((topAngle + bottomAngle) / 2) * 180 / Math.PI).toFixed(2));
}

export function getFixturePoint(corners, rect, u = 0, v = 0) {
  const normalized = normalizeFixtureCorners(corners, rect);
  const horizontal = finiteNumber(u);
  const vertical = finiteNumber(v);
  const { tl, tr, br, bl } = normalized;
  const bilinearFallback = () => {
    const top = {
      x: tl.x + (tr.x - tl.x) * horizontal,
      y: tl.y + (tr.y - tl.y) * horizontal,
    };
    const bottom = {
      x: bl.x + (br.x - bl.x) * horizontal,
      y: bl.y + (br.y - bl.y) * horizontal,
    };

    return {
      x: top.x + (bottom.x - top.x) * vertical,
      y: top.y + (bottom.y - top.y) * vertical,
    };
  };
  const deltaX1 = tr.x - br.x;
  const deltaX2 = bl.x - br.x;
  const deltaX3 = tl.x - tr.x + br.x - bl.x;
  const deltaY1 = tr.y - br.y;
  const deltaY2 = bl.y - br.y;
  const deltaY3 = tl.y - tr.y + br.y - bl.y;
  const determinant = deltaX1 * deltaY2 - deltaX2 * deltaY1;
  let projectiveX = 0;
  let projectiveY = 0;

  if (Math.abs(deltaX3) > 0.000001 || Math.abs(deltaY3) > 0.000001) {
    if (Math.abs(determinant) < 0.000001) {
      return bilinearFallback();
    }

    projectiveX = (deltaX3 * deltaY2 - deltaX2 * deltaY3) / determinant;
    projectiveY = (deltaX1 * deltaY3 - deltaX3 * deltaY1) / determinant;
  }

  const matrixA = tr.x - tl.x + projectiveX * tr.x;
  const matrixB = bl.x - tl.x + projectiveY * bl.x;
  const matrixD = tr.y - tl.y + projectiveX * tr.y;
  const matrixE = bl.y - tl.y + projectiveY * bl.y;
  const denominator = projectiveX * horizontal + projectiveY * vertical + 1;

  if (Math.abs(denominator) < 0.000001) {
    return bilinearFallback();
  }

  return {
    x: (matrixA * horizontal + matrixB * vertical + tl.x) / denominator,
    y: (matrixD * horizontal + matrixE * vertical + tl.y) / denominator,
  };
}

function getQuadProjectiveMatrix(points, width, height) {
  const { tl, tr, br, bl } = points;
  const deltaX1 = tr.x - br.x;
  const deltaX2 = bl.x - br.x;
  const deltaX3 = tl.x - tr.x + br.x - bl.x;
  const deltaY1 = tr.y - br.y;
  const deltaY2 = bl.y - br.y;
  const deltaY3 = tl.y - tr.y + br.y - bl.y;
  const determinant = deltaX1 * deltaY2 - deltaX2 * deltaY1;
  let projectiveX = 0;
  let projectiveY = 0;

  if (Math.abs(deltaX3) > 0.000001 || Math.abs(deltaY3) > 0.000001) {
    if (Math.abs(determinant) < 0.000001) {
      return null;
    }

    projectiveX = (deltaX3 * deltaY2 - deltaX2 * deltaY3) / determinant;
    projectiveY = (deltaX1 * deltaY3 - deltaX3 * deltaY1) / determinant;
  }

  const safeWidth = Math.max(1, width);
  const safeHeight = Math.max(1, height);

  // Row-major homography from shelf-local pixels to the four projected corners.
  return [
    (tr.x - tl.x + projectiveX * tr.x) / safeWidth,
    (bl.x - tl.x + projectiveY * bl.x) / safeHeight,
    tl.x,
    (tr.y - tl.y + projectiveX * tr.y) / safeWidth,
    (bl.y - tl.y + projectiveY * bl.y) / safeHeight,
    tl.y,
    projectiveX / safeWidth,
    projectiveY / safeHeight,
    1,
  ];
}

export function getShelfPerspectiveGeometry(fixture = {}, shelf = {}) {
  const rect = fixture.rect || {};
  const rectWidth = Math.max(1, finiteNumber(rect.width, 1));
  const rectHeight = Math.max(1, finiteNumber(rect.height, 1));
  const shelfX = finiteNumber(shelf.x);
  const shelfWidth = Math.max(1, finiteNumber(shelf.width, rectWidth));
  const shelfHeight = Math.max(1, finiteNumber(shelf.height, 1));
  const yOffset = clampDisplayBuilderNumber(
    shelf.yOffset,
    MIN_SHELF_Y_OFFSET,
    MAX_SHELF_Y_OFFSET
  );
  const shelfY = finiteNumber(shelf.y) + (yOffset / 100 * rectHeight);
  const leftU = shelfX / rectWidth;
  const rightU = (shelfX + shelfWidth) / rectWidth;
  const topV = shelfY / rectHeight;
  const bottomV = (shelfY + shelfHeight) / rectHeight;
  const points = {
    tl: getFixturePoint(fixture.corners, rect, leftU, topV),
    tr: getFixturePoint(fixture.corners, rect, rightU, topV),
    br: getFixturePoint(fixture.corners, rect, rightU, bottomV),
    bl: getFixturePoint(fixture.corners, rect, leftU, bottomV),
  };
  const projectedLeftCenter = {
    x: (points.tl.x + points.bl.x) / 2,
    y: (points.tl.y + points.bl.y) / 2,
  };
  const projectedRightCenter = {
    x: (points.tr.x + points.br.x) / 2,
    y: (points.tr.y + points.br.y) / 2,
  };
  const perspectiveAngle = Math.atan2(
    projectedRightCenter.y - projectedLeftCenter.y,
    projectedRightCenter.x - projectedLeftCenter.x
  ) * 180 / Math.PI;
  const skew = clampDisplayBuilderNumber(shelf.skew, MIN_SHELF_SKEW, MAX_SHELF_SKEW);
  const averageWidth = (
    Math.hypot(points.tr.x - points.tl.x, points.tr.y - points.tl.y)
    + Math.hypot(points.br.x - points.bl.x, points.br.y - points.bl.y)
  ) / 2;
  const skewRise = Math.tan(skew * Math.PI / 180) * averageWidth;

  points.tl.y -= skewRise / 2;
  points.bl.y -= skewRise / 2;
  points.tr.y += skewRise / 2;
  points.br.y += skewRise / 2;

  const leftCenter = {
    x: (points.tl.x + points.bl.x) / 2,
    y: (points.tl.y + points.bl.y) / 2,
  };
  const rightCenter = {
    x: (points.tr.x + points.br.x) / 2,
    y: (points.tr.y + points.br.y) / 2,
  };
  const topCenter = {
    x: (points.tl.x + points.tr.x) / 2,
    y: (points.tl.y + points.tr.y) / 2,
  };
  const bottomCenter = {
    x: (points.bl.x + points.br.x) / 2,
    y: (points.bl.y + points.br.y) / 2,
  };
  const center = {
    x: (leftCenter.x + rightCenter.x) / 2,
    y: (leftCenter.y + rightCenter.y) / 2,
  };
  const horizontalVector = {
    x: rightCenter.x - leftCenter.x,
    y: rightCenter.y - leftCenter.y,
  };
  const verticalVector = {
    x: bottomCenter.x - topCenter.x,
    y: bottomCenter.y - topCenter.y,
  };
  const horizontalMagnitude = Math.hypot(horizontalVector.x, horizontalVector.y);
  const verticalMagnitude = Math.hypot(verticalVector.x, verticalVector.y);
  const horizontalLength = Math.max(1, horizontalMagnitude);
  const horizontalUnit = horizontalMagnitude > 0.001
    ? {
      x: horizontalVector.x / horizontalMagnitude,
      y: horizontalVector.y / horizontalMagnitude,
    }
    : { x: 1, y: 0 };
  let verticalUnit = verticalMagnitude > 0.001
    ? {
      x: verticalVector.x / verticalMagnitude,
      y: verticalVector.y / verticalMagnitude,
    }
    : { x: 0, y: 1 };

  if (Math.abs(horizontalUnit.x * verticalUnit.y - horizontalUnit.y * verticalUnit.x) < 0.001) {
    verticalUnit = {
      x: -horizontalUnit.y,
      y: horizontalUnit.x,
    };

    if (verticalUnit.y < 0) {
      verticalUnit.x *= -1;
      verticalUnit.y *= -1;
    }
  }

  // Retained only as a safe fallback for a degenerate projective quad.
  const affineMatrix = [
    horizontalVector.x / shelfWidth,
    horizontalVector.y / shelfWidth,
    verticalVector.x / shelfHeight,
    verticalVector.y / shelfHeight,
    center.x - horizontalVector.x / 2 - verticalVector.x / 2,
    center.y - horizontalVector.y / 2 - verticalVector.y / 2,
  ];
  const projectiveMatrix = getQuadProjectiveMatrix(points, shelfWidth, shelfHeight);

  return {
    points,
    topCenter,
    bottomCenter,
    leftCenter,
    rightCenter,
    center,
    localWidth: shelfWidth,
    localHeight: shelfHeight,
    projectiveMatrix,
    affineMatrix,
    // Products are already sized in canvas pixels, so keep only the affine
    // directions here to avoid applying perspective scale twice.
    orientationMatrix: [
      horizontalUnit.x,
      horizontalUnit.y,
      verticalUnit.x,
      verticalUnit.y,
      0,
      0,
    ],
    perspectiveAngle,
    angle: Math.atan2(
      horizontalVector.y,
      horizontalVector.x
    ) * 180 / Math.PI,
    width: horizontalLength,
    height: Math.max(1, (
      Math.hypot(points.bl.x - points.tl.x, points.bl.y - points.tl.y)
      + Math.hypot(points.br.x - points.tr.x, points.br.y - points.tr.y)
    ) / 2),
  };
}

export function getShelvesInVerticalOrder(shelves = [], fixtureRect = {}) {
  const height = Number(fixtureRect.height || 0);
  return [...shelves].sort((first, second) => {
    return (Number(first.y || 0) + Number(first.yOffset || 0) * height / 100)
      - (Number(second.y || 0) + Number(second.yOffset || 0) * height / 100);
  });
}

export function swapShelfWithNeighbour(shelves, fixtureRect, shelfId, direction) {
  const ordered = getShelvesInVerticalOrder(shelves, fixtureRect);
  const index = ordered.findIndex((shelf) => String(shelf.id) === String(shelfId));
  const neighbourIndex = index + direction;
  if (index < 0 || ![-1, 1].includes(direction) || neighbourIndex < 0 || neighbourIndex >= ordered.length) {
    return null;
  }

  const shelf = ordered[index];
  const neighbour = ordered[neighbourIndex];
  const height = Number(fixtureRect.height || 0);
  const offset = (item) => Number(item.yOffset || 0) * height / 100;
  ordered[index] = { ...neighbour, y: Number(shelf.y || 0) + offset(shelf) - offset(neighbour) };
  ordered[neighbourIndex] = { ...shelf, y: Number(neighbour.y || 0) + offset(neighbour) - offset(shelf) };
  return ordered.map((item, shelfIndex) => ({ ...item, number: shelfIndex + 1 }));
}

export function fitShelvesToFixture(shelves = [], fixtureRect = {}) {
  const normalizedShelves = shelves.filter(Boolean);
  const count = normalizedShelves.length;

  if (!count) {
    return [];
  }

  const fixtureHeight = Math.max(1, Number(fixtureRect.height || 1));

  return normalizedShelves.map((shelf, index) => {
    const y = Math.round(index * fixtureHeight / count);
    const nextY = Math.round((index + 1) * fixtureHeight / count);

    return {
      ...shelf,
      number: index + 1,
      x: 0,
      y,
      width: Number(fixtureRect.width || shelf.width || 0),
      height: Math.max(1, nextY - y),
      products: Array.isArray(shelf.products) ? shelf.products : [],
    };
  });
}

export function fitShelvesVerticallyToFixture(shelves = [], fixtureRect = {}) {
  const normalizedShelves = shelves.filter(Boolean);
  const fittedShelves = fitShelvesToFixture(normalizedShelves, fixtureRect);

  return normalizedShelves.map((shelf, index) => {
    const fittedShelf = fittedShelves[index] || {};
    const hasX = shelf.x !== undefined && shelf.x !== null;
    const hasWidth = shelf.width !== undefined && shelf.width !== null;

    return clampShelfToFixture({
      ...shelf,
      number: index + 1,
      x: hasX ? Number(shelf.x || 0) : fittedShelf.x,
      y: fittedShelf.y,
      width: hasWidth ? Number(shelf.width || 1) : fittedShelf.width,
      height: fittedShelf.height,
      products: Array.isArray(shelf.products) ? shelf.products : [],
    }, fixtureRect);
  });
}

export function transformShelvesWithFixture(shelves = [], previousRect = {}, nextRect = {}) {
  const normalizedShelves = shelves.filter(Boolean);
  const fittedShelves = fitShelvesToFixture(normalizedShelves, nextRect);
  const scaleX = Number(nextRect.width || 1) / Math.max(1, Number(previousRect.width || nextRect.width || 1));
  const scaleY = Number(nextRect.height || 1) / Math.max(1, Number(previousRect.height || nextRect.height || 1));

  return normalizedShelves.map((shelf, index) => {
    const fittedShelf = fittedShelves[index] || {};
    const hasX = shelf.x !== undefined && shelf.x !== null;
    const hasY = shelf.y !== undefined && shelf.y !== null;
    const hasWidth = shelf.width !== undefined && shelf.width !== null;
    const hasHeight = shelf.height !== undefined && shelf.height !== null;

    return clampShelfToFixture({
      ...shelf,
      number: index + 1,
      x: hasX ? Number(shelf.x || 0) * scaleX : fittedShelf.x,
      y: hasY ? Number(shelf.y || 0) * scaleY : fittedShelf.y,
      width: hasWidth ? Number(shelf.width || 1) * scaleX : fittedShelf.width,
      height: hasHeight ? Number(shelf.height || 1) * scaleY : fittedShelf.height,
      products: Array.isArray(shelf.products) ? shelf.products : [],
    }, nextRect);
  });
}

export function clampShelfToFixture(shelf = {}, fixtureRect = {}) {
  const fixtureLeft = Number(fixtureRect.left || 0);
  const fixtureTop = Number(fixtureRect.top || 0);
  const fixtureWidth = Math.max(1, Number(fixtureRect.width || 1));
  const canvasWidth = DISPLAY_BUILDER_CANVAS_WIDTH;
  const canvasHeight = DISPLAY_BUILDER_CANVAS_HEIGHT;
  const shelfWidth = Math.min(
    Math.max(1, Number(shelf.width || fixtureWidth)),
    canvasWidth
  );
  const shelfHeight = Math.min(
    Math.max(1, Number(shelf.height || 1)),
    canvasHeight
  );
  const minX = -fixtureLeft;
  const minY = -fixtureTop;
  const maxX = canvasWidth - fixtureLeft - shelfWidth;
  const maxY = canvasHeight - fixtureTop - shelfHeight;
  const x = Math.min(Math.max(minX, Number(shelf.x || 0)), maxX);
  const y = Math.min(Math.max(minY, Number(shelf.y || 0)), maxY);

  return {
    ...shelf,
    x,
    y,
    width: shelfWidth,
    height: shelfHeight,
    products: Array.isArray(shelf.products) ? shelf.products : [],
  };
}

export const DISPLAY_BUILDER_MIN_VISIBLE_MARGIN = 50;

const OVERLAP_CLIP_SEED_HALF_EXTENT = 10000;
const OVERLAP_EPSILON = 0.01;

function getCanvasCorners(canvasWidth, canvasHeight) {
  return [
    { x: 0, y: 0 },
    { x: canvasWidth, y: 0 },
    { x: canvasWidth, y: canvasHeight },
    { x: 0, y: canvasHeight },
  ];
}

function normalizeVector(vector) {
  const length = Math.hypot(vector.x, vector.y);

  return length > 0.000001
    ? { x: vector.x / length, y: vector.y / length }
    : { x: 1, y: 0 };
}

function getCanvasAxesAndObjectEdgeNormalAxes(objectCorners) {
  const [tl, tr, , bl] = objectCorners;

  return [
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    normalizeVector({ x: -(tr.y - tl.y), y: tr.x - tl.x }),
    normalizeVector({ x: -(bl.y - tl.y), y: bl.x - tl.x }),
  ];
}

function projectPointsOntoAxis(points, axis) {
  let min = Infinity;
  let max = -Infinity;

  points.forEach((point) => {
    const projection = point.x * axis.x + point.y * axis.y;

    min = Math.min(min, projection);
    max = Math.max(max, projection);
  });

  return { min, max };
}

function buildMinimumOverlapAxisConstraintsCappedByProjectionLengths(
  objectCorners,
  canvasWidth,
  canvasHeight,
  margin
) {
  const canvasCorners = getCanvasCorners(canvasWidth, canvasHeight);

  return getCanvasAxesAndObjectEdgeNormalAxes(objectCorners).map((axis) => {
    const canvasProjection = projectPointsOntoAxis(canvasCorners, axis);
    const objectProjection = projectPointsOntoAxis(objectCorners, axis);
    const objectExtent = objectProjection.max - objectProjection.min;
    const canvasExtent = canvasProjection.max - canvasProjection.min;
    const marginCappedByBothProjectionLengths = Math.min(margin, objectExtent, canvasExtent);

    return {
      axis,
      lower: canvasProjection.min + marginCappedByBothProjectionLengths - objectProjection.max,
      upper: canvasProjection.max - marginCappedByBothProjectionLengths - objectProjection.min,
    };
  });
}

function clipPolygonByHalfPlaneWhereNormalDotPointIsAtMostOffset(polygon, normal, offset) {
  if (!polygon.length) {
    return [];
  }

  const result = [];

  polygon.forEach((current, index) => {
    const previous = polygon[(index - 1 + polygon.length) % polygon.length];
    const currentValue = current.x * normal.x + current.y * normal.y;
    const previousValue = previous.x * normal.x + previous.y * normal.y;
    const currentInside = currentValue <= offset + OVERLAP_EPSILON;
    const previousInside = previousValue <= offset + OVERLAP_EPSILON;

    if (currentInside !== previousInside) {
      const denominator = currentValue - previousValue;
      const t = denominator !== 0 ? (offset - previousValue) / denominator : 0;

      result.push({
        x: previous.x + t * (current.x - previous.x),
        y: previous.y + t * (current.y - previous.y),
      });
    }

    if (currentInside) {
      result.push(current);
    }
  });

  return result;
}

function intersectAxisConstraintsIntoFeasibleTranslationPolygon(constraints) {
  let feasiblePolygon = [
    { x: -OVERLAP_CLIP_SEED_HALF_EXTENT, y: -OVERLAP_CLIP_SEED_HALF_EXTENT },
    { x: OVERLAP_CLIP_SEED_HALF_EXTENT, y: -OVERLAP_CLIP_SEED_HALF_EXTENT },
    { x: OVERLAP_CLIP_SEED_HALF_EXTENT, y: OVERLAP_CLIP_SEED_HALF_EXTENT },
    { x: -OVERLAP_CLIP_SEED_HALF_EXTENT, y: OVERLAP_CLIP_SEED_HALF_EXTENT },
  ];

  constraints.forEach(({ axis, lower, upper }) => {
    feasiblePolygon = clipPolygonByHalfPlaneWhereNormalDotPointIsAtMostOffset(feasiblePolygon, axis, upper);
    feasiblePolygon = clipPolygonByHalfPlaneWhereNormalDotPointIsAtMostOffset(
      feasiblePolygon,
      { x: -axis.x, y: -axis.y },
      -lower
    );
  });

  return feasiblePolygon;
}

function isOriginInsidePolygon(polygon) {
  if (polygon.length < 3) {
    return false;
  }

  let sign = 0;

  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const cross = (b.x - a.x) * (0 - a.y) - (b.y - a.y) * (0 - a.x);

    if (Math.abs(cross) < OVERLAP_EPSILON) {
      continue;
    }

    const currentSign = cross > 0 ? 1 : -1;

    if (sign === 0) {
      sign = currentSign;
    } else if (currentSign !== sign) {
      return false;
    }
  }

  return true;
}

function closestPointOnSegmentToOrigin(a, b) {
  const deltaX = b.x - a.x;
  const deltaY = b.y - a.y;
  const lengthSquared = deltaX * deltaX + deltaY * deltaY;

  if (lengthSquared < 0.000001) {
    return { x: a.x, y: a.y };
  }

  const t = Math.min(1, Math.max(0, -(a.x * deltaX + a.y * deltaY) / lengthSquared));

  return { x: a.x + t * deltaX, y: a.y + t * deltaY };
}

function getNearestPointToOriginOrZeroIfOriginIsInsidePolygon(polygon) {
  if (!polygon.length) {
    return null;
  }

  if (isOriginInsidePolygon(polygon)) {
    return { x: 0, y: 0 };
  }

  let closest = null;
  let closestDistance = Infinity;

  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const candidate = closestPointOnSegmentToOrigin(a, b);
    const distance = Math.hypot(candidate.x, candidate.y);

    if (distance < closestDistance) {
      closestDistance = distance;
      closest = candidate;
    }
  }

  return closest;
}

function satisfiesEveryAxisConstraint(point, constraints) {
  return constraints.every(({ axis, lower, upper }) => {
    const value = point.x * axis.x + point.y * axis.y;

    return value >= lower - OVERLAP_EPSILON && value <= upper + OVERLAP_EPSILON;
  });
}

export function isObjectFullyOutsideCanvas(
  objectCorners,
  canvasWidth = DISPLAY_BUILDER_CANVAS_WIDTH,
  canvasHeight = DISPLAY_BUILDER_CANVAS_HEIGHT
) {
  const canvasCorners = getCanvasCorners(canvasWidth, canvasHeight);

  return getCanvasAxesAndObjectEdgeNormalAxes(objectCorners).some((axis) => {
    const canvasProjection = projectPointsOntoAxis(canvasCorners, axis);
    const objectProjection = projectPointsOntoAxis(objectCorners, axis);
    const overlap = Math.min(canvasProjection.max, objectProjection.max)
      - Math.max(canvasProjection.min, objectProjection.min);

    return overlap <= 0;
  });
}

export function getMinimumOverlapCorrection(
  objectCorners,
  canvasWidth = DISPLAY_BUILDER_CANVAS_WIDTH,
  canvasHeight = DISPLAY_BUILDER_CANVAS_HEIGHT,
  margin = DISPLAY_BUILDER_MIN_VISIBLE_MARGIN
) {
  const constraints = buildMinimumOverlapAxisConstraintsCappedByProjectionLengths(
    objectCorners,
    canvasWidth,
    canvasHeight,
    margin
  );
  const feasiblePolygon = intersectAxisConstraintsIntoFeasibleTranslationPolygon(constraints);
  const point = getNearestPointToOriginOrZeroIfOriginIsInsidePolygon(feasiblePolygon);

  if (!point || !satisfiesEveryAxisConstraint(point, constraints)) {
    return { dx: 0, dy: 0 };
  }

  return { dx: point.x, dy: point.y };
}

export function getGroupMinimumOverlapCorrection(
  membersCorners,
  canvasWidth = DISPLAY_BUILDER_CANVAS_WIDTH,
  canvasHeight = DISPLAY_BUILDER_CANVAS_HEIGHT,
  margin = DISPLAY_BUILDER_MIN_VISIBLE_MARGIN
) {
  const constraints = membersCorners.reduce((constraintsForEveryMember, corners) => (
    constraintsForEveryMember.concat(
      buildMinimumOverlapAxisConstraintsCappedByProjectionLengths(corners, canvasWidth, canvasHeight, margin)
    )
  ), []);

  if (!constraints.length) {
    return { dx: 0, dy: 0, feasible: true };
  }

  const feasiblePolygon = intersectAxisConstraintsIntoFeasibleTranslationPolygon(constraints);
  const point = getNearestPointToOriginOrZeroIfOriginIsInsidePolygon(feasiblePolygon);

  if (!point || !satisfiesEveryAxisConstraint(point, constraints)) {
    return { dx: 0, dy: 0, feasible: false };
  }

  return { dx: point.x, dy: point.y, feasible: true };
}

export function defaultFixtureState() {
  const fixtures = {};
  const fixture_order = DEFAULT_LAYOUT_FIXTURES.map(({ key }) => key);

  DEFAULT_LAYOUT_FIXTURES.forEach((fixture) => {
    const rect = rectFromPercent(fixture);

    fixtures[fixture.key] = {
      label: fixture.label,
      rect,
      corners: rectToFixtureCorners(rect),
      angle: 0,
      shelfCount: fixture.shelfCount,
      shelves: [],
    };
  });

  return {
    active_fixture: 'center',
    fixture_order,
    fixtures,
  };
}

export function getFixtureOrder(state = {}) {
  const fixtures = state.fixtures || {};
  const keys = Object.keys(fixtures);

  if (Array.isArray(state.fixture_order) && state.fixture_order.length) {
    return [
      ...state.fixture_order.filter((key) => fixtures[key]),
      ...keys.filter((key) => !state.fixture_order.includes(key)),
    ];
  }

  const defaultOrder = DEFAULT_LAYOUT_FIXTURES.map(({ key }) => key);

  return [
    ...defaultOrder.filter((key) => fixtures[key]),
    ...keys.filter((key) => !defaultOrder.includes(key)),
  ];
}

function isEmptyLegacyDefault(state = {}) {
  const fixtures = state.fixtures || {};
  const keys = Object.keys(fixtures);

  if (!keys.length || Array.isArray(state.fixture_order)) {
    return false;
  }

  if (!keys.every((key) => ['left', 'center', 'right'].includes(key))) {
    return false;
  }

  return keys.every((key) => {
    return !Array.isArray(fixtures[key]?.shelves) || fixtures[key].shelves.length === 0;
  });
}

export function normalizeDisplayBuilderState(rawState = {}) {
  const incoming = cloneDisplayBuilderValue(rawState);

  if (
    !incoming.fixtures ||
    (!Object.keys(incoming.fixtures).length && !Array.isArray(incoming.fixture_order)) ||
    isEmptyLegacyDefault(incoming)
  ) {
    return {
      ...incoming,
      ...defaultFixtureState(),
      productSpacing: normalizeShelfProductSpacing(
        incoming.productSpacing,
        DEFAULT_PRODUCT_SPACING
      ),
      logos: incoming.logos || [],
    };
  }

  const fixtures = incoming.fixtures || {};
  const fixture_order = getFixtureOrder(incoming);
  const active_fixture = fixture_order.includes(incoming.active_fixture)
    ? incoming.active_fixture
    : fixture_order[0] || null;

  fixture_order.forEach((key, index) => {
    const source = fixtures[key] || {};
    const defaultFixture = DEFAULT_LAYOUT_FIXTURES[index] || DEFAULT_LAYOUT_FIXTURES[0];
    const rect = source.rect || rectFromPercent(defaultFixture);
    const corners = normalizeFixtureCorners(source.corners, rect);

    fixtures[key] = {
      ...source,
      label: source.label || defaultFixture.label || `Fixture ${index + 1}`,
      rect,
      corners,
      angle: getFixtureAngle(corners, rect),
      shelfCount: source.shelfCount || source.shelves?.length || defaultFixture.shelfCount || DEFAULT_SHELF_COUNT,
      shelves: Array.isArray(source.shelves)
        ? source.shelves.map((shelf) => ({
          ...shelf,
          depth: normalizeShelfDepth(shelf?.depth),
          yOffset: clampDisplayBuilderNumber(
            shelf?.yOffset,
            MIN_SHELF_Y_OFFSET,
            MAX_SHELF_Y_OFFSET
          ),
          skew: clampDisplayBuilderNumber(
            shelf?.skew,
            MIN_SHELF_SKEW,
            MAX_SHELF_SKEW
          ),
          productSpacing: normalizeShelfProductSpacing(
            shelf?.productSpacing ?? shelf?.product_spacing
          ),
        }))
        : [],
    };
  });

  return {
    ...incoming,
    active_fixture,
    fixture_order,
    fixtures,
    productSpacing: normalizeShelfProductSpacing(
      incoming.productSpacing,
      DEFAULT_PRODUCT_SPACING
    ),
    logos: incoming.logos || [],
  };
}
